import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, CheckCircle2 } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useCurrentCompany, switchCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';
import { Button } from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';

export default function CompanySelectorPage() {
  const navigate = useNavigate();
  const { allCompanies, isLoading } = useCurrentCompany();
  const [switching, setSwitching] = useState<string | null>(null);
  const hasAutoSelectedRef = useRef(false); // Prevent infinite loop

  const handleSelectCompany = useCallback(async (companyId: string) => {
    // Prevent multiple simultaneous switches
    setSwitching(prev => {
      if (prev) return prev; // Already switching, ignore
      return companyId;
    });
    
    // Get the role for the selected company IMMEDIATELY (no need to wait - role is already in allCompanies)
    const selectedCompany = allCompanies.find(c => c.company_id === companyId);
    const role = selectedCompany?.role;
    
    // Determine redirect path based on role (do this before async operations)
    const redirectPath = (role === 'owner' || role === 'admin') 
      ? '/dashboard' 
      : '/student-landing';
    
    try {
      // Switch company and refresh session in the background
      // Navigate immediately - don't wait for these operations
      await switchCompany(companyId);
      await supabase.auth.refreshSession();
      
      // Navigate immediately after getting the role (don't wait for session refresh)
      navigate(redirectPath);
    } catch (error) {
      console.error('Error switching company:', error);
      // Even if switch fails, navigate to the correct page based on role
      navigate(redirectPath);
      setSwitching(null);
    }
  }, [allCompanies, navigate]);

  useEffect(() => {
    // If user only has one company, auto-select it (only once)
    if (!isLoading && allCompanies.length === 1 && !hasAutoSelectedRef.current && !switching) {
      hasAutoSelectedRef.current = true;
      const companyId = allCompanies[0].company_id;
      handleSelectCompany(companyId);
    }
  }, [allCompanies, isLoading, switching, handleSelectCompany]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  if (allCompanies.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">No Companies Found</h1>
          <p className="text-gray-400 mb-6">You don&apos;t have access to any companies.</p>
          <Button onClick={() => navigate('/create-gym')}>
            Create a Gym
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <Helmet>
        <title>Select Gym - RollOps</title>
        <meta name="robots" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
        <meta name="googlebot" content="noindex, nofollow, noimageindex, nosnippet, noarchive, notranslate" />
        <meta name="bingbot" content="noindex, nofollow, noimageindex, nosnippet, noarchive" />
      </Helmet>
      
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Select a Gym</h1>
          <p className="text-gray-400">
            You have multiple affiliations. Please choose which one you&apos;d like to access.
          </p>
        </div>

        <div className="space-y-4">
          {allCompanies.map((companyData) => {
            const isSwitching = switching === companyData.company_id;
            return (
              <Card
                key={companyData.company_id}
                className="cursor-pointer hover:bg-[#1a1a1a] transition-colors"
                onClick={() => !isSwitching && handleSelectCompany(companyData.company_id)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {companyData.company.logo_url ? (
                        <img
                          src={companyData.company.logo_url}
                          alt={companyData.company.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-[#9B1421] flex items-center justify-center">
                          <Building2 className="w-8 h-8 text-white" />
                        </div>
                      )}
                      <div>
                        <h3 className="text-xl font-semibold text-white">
                          {companyData.company.name}
                        </h3>
                        <p className="text-gray-400 capitalize">
                          Role: {companyData.role}
                        </p>
                      </div>
                    </div>
                    {isSwitching ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#9B1421]" />
                    ) : (
                      <CheckCircle2 className="w-6 h-6 text-rollops-accent" />
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}

